import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useLanguage } from "../context/LanguageContext";
import { Calendar, MapPin, Clock, ArrowLeft, Heart, Share2, Copy, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { API_URL as API } from "../config";

const GOFUNDME_URL = "https://gofund.me/274183352";

export default function EventDetailPage() {
  const { eventId } = useParams();
  const { lang, t } = useLanguage();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/events/${eventId}`)
      .then(r => {
        if (!r.ok) throw new Error("Not found");
        return r.json();
      })
      .then(setEvent)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [eventId]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleNativeShare = () => {
    if (navigator.share) {
      navigator.share({ title: event?.title, text: event?.description, url: shareUrl });
    } else {
      handleCopy();
    }
  };

  if (loading) return (
    <section className="pt-32 pb-20 min-h-screen bg-gradient-to-b from-[#F0F9FF] to-white">
      <div className="max-w-3xl mx-auto px-4 flex items-center justify-center py-16">
        <div className="animate-spin w-8 h-8 border-4 border-[#87CEEB] border-t-transparent rounded-full" />
      </div>
    </section>
  );

  if (notFound) return (
    <section className="pt-32 pb-20 min-h-screen bg-gradient-to-b from-[#F0F9FF] to-white">
      <div className="max-w-3xl mx-auto px-4 text-center py-16">
        <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-800 font-['Kanit'] mb-2">{lang === "es" ? "Evento no encontrado" : "Event Not Found"}</h1>
        <p className="text-gray-500 mb-6">{lang === "es" ? "Este evento ya no esta disponible." : "This event is no longer available."}</p>
        <Link to="/events" className="text-[#87CEEB] font-medium hover:underline">{lang === "es" ? "Ver todos los eventos" : "View all events"}</Link>
      </div>
    </section>
  );

  const dateFormatted = event.date ? new Date(event.date + "T00:00:00").toLocaleDateString(lang === "es" ? "es-NI" : "en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" }) : event.date;

  return (
    <>
      <Helmet>
        <title>{event.title} - Curious 123</title>
        <meta name="description" content={event.description} />
        <meta property="og:title" content={event.title} />
        <meta property="og:description" content={event.description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={shareUrl} />
        {event.image_url && <meta property="og:image" content={event.image_url} />}
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Curious 123" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={event.title} />
        <meta name="twitter:description" content={event.description} />
        {event.image_url && <meta name="twitter:image" content={event.image_url} />}
      </Helmet>

      <section className="pt-32 pb-20 min-h-screen bg-gradient-to-b from-[#F0F9FF] to-white" data-testid="event-detail-page">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Link to="/events" className="inline-flex items-center text-gray-600 hover:text-black mb-8 transition-colors text-sm font-medium" data-testid="back-to-events">
            <ArrowLeft className="w-4 h-4 mr-2" /> {lang === "es" ? "Todos los Eventos" : "All Events"}
          </Link>

          {event.image_url && (
            <div className="rounded-2xl overflow-hidden mb-8 shadow-lg">
              <img src={event.image_url} alt={event.title} className="w-full h-64 sm:h-80 object-cover" />
            </div>
          )}

          <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-[#87CEEB] text-black px-3 py-1.5 rounded-lg text-sm font-bold font-['Kanit']">
                {event.date ? new Date(event.date + "T00:00:00").toLocaleDateString(lang === "es" ? "es-NI" : "en-US", { month: "short", day: "numeric" }) : ""}
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 font-['Kanit'] mb-4" data-testid="event-detail-title">{event.title}</h1>

            <div className="flex flex-wrap gap-4 mb-6 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#87CEEB]" />
                <span>{dateFormatted}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#87CEEB]" />
                <span>{event.time}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#87CEEB]" />
                <span>{event.location}</span>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-6 mb-8">
              <p className="text-gray-700 leading-relaxed text-base">{event.description}</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <a href={GOFUNDME_URL} target="_blank" rel="noopener noreferrer" className="flex-1">
                <Button className="donate-btn rounded-full w-full py-3 font-semibold uppercase tracking-wider" data-testid="event-donate-btn">
                  <Heart className="w-4 h-4 mr-2" /> {lang === "es" ? "Donar Ahora" : "Donate Now"}
                </Button>
              </a>
              <button onClick={handleNativeShare} data-testid="event-share-btn"
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-gray-300 hover:border-[#87CEEB] hover:bg-[#87CEEB]/5 transition-colors text-sm font-medium">
                <Share2 className="w-4 h-4" /> {lang === "es" ? "Compartir" : "Share"}
              </button>
              <button onClick={handleCopy} data-testid="event-copy-link-btn"
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-gray-300 hover:border-[#87CEEB] hover:bg-[#87CEEB]/5 transition-colors text-sm font-medium">
                {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                {copied ? (lang === "es" ? "Copiado!" : "Copied!") : (lang === "es" ? "Copiar Enlace" : "Copy Link")}
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
